import { useState, useCallback, useEffect, useMemo } from 'react'
import { flushSync } from 'react-dom'
import { ButtonPrimary } from '../../../../../../../baseComponents/Button'
import CustomError from '../../../../../../../baseComponents/CustomError'
import { ModalSmallMedium } from '../../../../../../../baseComponents/Modal'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { checkSchedulesAllOk } from '../../../../../../../utils/globals/schedule'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import METurnSingleDay from './METurnSingleDay'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { WCalTurnScheduleDictTY, WCalTurnWeekTy } from '../types'
import { getConfigParam } from '../../../../../../../utils/reducers/getters'
import { CustomSelectWithSearchBar } from '../../../../../../../forms/CustomSelect'
import { EmployeeModelType } from '@teinor/erp/types/company/userInner/employee'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { checkIfDailyHoursIsSmallerModalCEWTurn } from './functions'
import { changeErrorMessage, changeSuccessMessage } from '../../../../../../../utils/reducers/reduxDispatch'

interface ModalCEWCalTurnProps extends ModalCEWCalTurnLogicProps {
    week: WCalTurnWeekTy
    baseJourney: WCalTurnScheduleDictTY
}

const ModalCEWCalTurn: React.FC<ModalCEWCalTurnProps> = ({ setOpenedModalSchedule, schedules, employeeData, week, getData, selectedWorkingCenter, baseJourney }) => {
    const { mySchedules, checkAllOkAndSubmit, setMySchedules, setWorkingCenter, workingCenter, employeeWorkingCenters, punchInWithWorkingCenter } = useModalCEWCalTurnLogic({ schedules, setOpenedModalSchedule, getData, selectedWorkingCenter, employeeData })
    const renderThis: JSX.Element[] = []
    const { t } = useTwinTranslation()
    if (punchInWithWorkingCenter && !employeeWorkingCenters) {
        return <ModalSmallMedium className='flex flex-col' opened={true} setOpened={setOpenedModalSchedule} onClickOut={false}>
            <div className=' modal_negative_margin flex flex-auto flex-col'>
                <div className='modal_schedule_container flex-auto overflow-auto h-1'>
                    <div className='flex'>
                        <h2 className='mr-auto'>{employeeData.fullname_short}</h2>
                    </div>
                    <CustomError textError={t('thisEmployeeDontHaveWorkingCentersToAssignSchedule','Este empleado no tiene ningún centro de trabajo para poder asignarle un horario')} className='my-20' />
                </div>
            </div>
        </ModalSmallMedium>
    }
    
    for (const day of week.weekDates) {
        let mySchedule = mySchedules?.[day]?.[workingCenter]
        if (!mySchedule) {
            const newTurn = JSON.parse(JSON.stringify(baseJourney[day]))
            mySchedule = {
                ...newTurn,
                isBaseTurn: true,
                isTurnDay: false,
                schedule: { ...newTurn.journey, errors: [], workingTime: newTurn.journey?.workingTime || [], flexible: newTurn.journey?.flexible || false },
            }
        }
        const active = mySchedule && mySchedule.isTurnDay? true : false

        renderThis.push(<METurnSingleDay key={day + workingCenter} {...{ day: day, setMySchedules, mySchedule: mySchedule, workingCenter, active, punchInWithWorkingCenter, baseJourney }} />)
    }
    return (
        <ModalSmallMedium className='flex flex-col' opened={true} setOpened={setOpenedModalSchedule} onClickOut={false}>
            <div className='flex flex-auto flex-col '>
                <div className='modal_schedule_container flex-auto h-1 flex flex-col'>
                    <div className='flex items-end mb-10'>
                        <h2 className='mr-auto'>{employeeData.fullname_short}</h2>
                        {punchInWithWorkingCenter ?
                            <CustomSelectWithSearchBar label={t('workingCenter', 'Centro de trabajo')} items={employeeWorkingCenters || {}} className='working_calendar_turns_select' onChange={setWorkingCenter} value={workingCenter} />
                        : null}
                    </div>
                    <div className='flex-auto  modal_negative_margin h-1 flex flex-col'>
                        <div className='overflow-auto'>
                            {renderThis}
                            <div className='flex justify-end my-20 mr-10'>
                                <ButtonPrimary onClick={checkAllOkAndSubmit}>
                                    <TwinTrans transKey='save'>Guardar</TwinTrans>
                                </ButtonPrimary>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ModalSmallMedium>
    )
}

interface ModalCEWCalTurnLogicProps {
    schedules: { [key: string]: WCalTurnScheduleDictTY }
    setOpenedModalSchedule: React.Dispatch<React.SetStateAction<true | null>>
    getData: () => Promise<void>
    selectedWorkingCenter: string
    employeeData: EmployeeModelType
}

const useModalCEWCalTurnLogic = ({ schedules, setOpenedModalSchedule, getData, selectedWorkingCenter, employeeData }: ModalCEWCalTurnLogicProps) => {
    const [mySchedules, setMySchedules] = useState<{ [key: string]: WCalTurnScheduleDictTY }>(JSON.parse(JSON.stringify(schedules)))
    const punchInWithWorkingCenter = useMemo(() => getConfigParam('punchInWithWorkingCenter') === 'on', [])
    const [workingCenter, setWorkingCenter] = useState<string>(punchInWithWorkingCenter ? selectedWorkingCenter || String(employeeData?.defaultWorkingCenterId || '') : '')
    const [employeeWorkingCenters, setEmployeeWorkingCenters] = useState<TwinDictionary | null>(null)
    const { t } = useTwinTranslation()
    const errorHours = t('conflictInHoursSelectedCheckRedFields', 'Hay un conflicto en las horas seleccionadas. Revisa los campos en rojo.')
    const errorDailyHours = t('conflictInDailyHoursCheckNotExceedTotalDay', 'Hay un conflicto en las horas diarias. Revisa que no superen el total de cada día.')
    const successMsg = t('successfullyCompleted', 'Realizado correctamente')

    const checkAllOkAndSubmit = useCallback(async () => {
        await flushSync(async () => {
            let hasError = false
            let checkedSchedules: { [key: string]: WCalTurnScheduleDictTY } | false = false
            await setMySchedules((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                const schedules: { [key: string]: WCalTurnScheduleDictTY } = {}
                for (const day in copyOld) {
                    for (const WorkingCenterId in copyOld[day]) {
                        const myDaySchedule = copyOld[day][WorkingCenterId]
                        if ((!punchInWithWorkingCenter && !WorkingCenterId) || (punchInWithWorkingCenter && WorkingCenterId)) {
                            const response = checkSchedulesAllOk(myDaySchedule?.schedule)
                            if (response.hasError) {
                                hasError = true
                            } else if (myDaySchedule.isTurnDay || myDaySchedule.TurnId) {
                                if (WorkingCenterId) {
                                    myDaySchedule.WorkingCenterId = parseInt(WorkingCenterId)
                                }
                                if (!schedules[day]) {
                                    schedules[day] = {}
                                }
                                schedules[day][WorkingCenterId] = myDaySchedule
                            }
                        }
                    }
                }
                if (!hasError) {
                    checkedSchedules = schedules
                }
                return { ...copyOld }
            })
            if (hasError) {
                const modal = document.querySelector('.modal_schedule_container ');
                if (modal) {
                    modal.scrollTop = 0
                }
                changeErrorMessage(errorHours)
                return false
            }
            if (checkedSchedules) {
                const checkDailyHours = checkIfDailyHoursIsSmallerModalCEWTurn(checkedSchedules)
                if (checkDailyHours.hasError) {
                    const modal = document.querySelector('.modal_schedule_container ');
                    if (modal) {
                        modal.scrollTop = 0
                    }
                    changeErrorMessage(errorDailyHours)
                    return false
                } else {
                    const result = await twinFetchPostJSON('/api/app/workingCalendar/workingCalendarTurn/bulkUpdateWCalTurn', {
                        turns: checkedSchedules
                    })
                    if (result) {
                        setOpenedModalSchedule(null)
                        changeSuccessMessage(successMsg)
                        getData()
                    }
                }
            }
        })
    }, [setMySchedules, errorHours, errorDailyHours, getData, setOpenedModalSchedule, punchInWithWorkingCenter, successMsg])

    const getEmployeeWorkingCenters = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/workingCenter/workingCenterEmployee/getAllWorkingCentersByEmployee', { EmployeeId: employeeData.id })
        if (result && result.length) {
            const allWorkingCenters = dictionaryComplexFromJsonArr(result || [])
            setEmployeeWorkingCenters(allWorkingCenters)
        }
    }, [setEmployeeWorkingCenters, employeeData])

    useEffect(() => {
        if (punchInWithWorkingCenter) {
            getEmployeeWorkingCenters()
        }
    }, [getEmployeeWorkingCenters, punchInWithWorkingCenter])

    return { mySchedules, checkAllOkAndSubmit, setMySchedules, setWorkingCenter, workingCenter, employeeWorkingCenters, punchInWithWorkingCenter }
}

export default ModalCEWCalTurn